import {
  LEGACY_RISK_MATRIX,
  type RaCategory,
  type RaDepartment,
  type RaMatrixDefinition,
  type RiskControl,
  type RiskHazard,
  type RiskLevel,
  type RiskRating,
  raMatrixIssues,
  riskSummary,
} from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import type { RiskStatus, RiskSummary } from './types.js';

/**
 * Risk ekranının saf görünüm modeli (ADR 0050 §7): özet kartları, matris sayıları, süzgeç,
 * sıralama ve CSV. Arayüz bileşenleri yalnız bunları çizer.
 */

export type MatrixMode = 'initial' | 'residual';

/**
 * Seviyenin rozet tonu (referans: Low yeşil, Medium sarı, High, Very High ve Critical kırmızı).
 */
export function levelTone(level: RiskLevel): StatusTone {
  if (level === 'very_low' || level === 'low') return 'success';
  if (level === 'medium') return 'warning';
  return 'danger';
}

export function statusTone(status: RiskStatus): StatusTone {
  switch (status) {
    case 'approved':
      return 'success';
    case 'pending':
      return 'info';
    case 'returned':
      return 'warning';
    default:
      return 'neutral';
  }
}

const active = (items: readonly RiskSummary[]) => items.filter((i) => i.status !== 'withdrawn');

/**
 * Özet kartları: toplam, yüksek (High + Very High + Critical), orta, düşük (Low + Very Low); geri
 * çekilen hariç. Seviye her değerlendirmenin kendi matrisinden gelir (ADR 0085 §4).
 */
export function riskKpis(items: readonly RiskSummary[]) {
  const list = active(items);
  const count = (levels: readonly RiskLevel[]) =>
    list.filter((i) => i.initial && levels.includes(i.initial.level)).length;
  return {
    total: list.length,
    high: count(['high', 'very_high', 'critical']),
    medium: count(['medium']),
    low: count(['low', 'very_low']),
    unscored: list.filter((i) => !i.initial).length,
  };
}

/** `[olasılık][şiddet]` → değerlendirme sayısı (1–5); geri çekilen hariç. */
export function matrixCounts(items: readonly RiskSummary[], mode: MatrixMode): number[][] {
  const grid = Array.from({ length: 6 }, () => Array.from({ length: 6 }, () => 0));
  for (const item of active(items)) {
    const r = mode === 'initial' ? item.initial : item.residual;
    if (!r) continue;
    (grid[r.likelihood] as number[])[r.severity] =
      ((grid[r.likelihood] as number[])[r.severity] ?? 0) + 1;
  }
  return grid;
}

export interface RiskFilter {
  vesselId: string | null;
  department: RaDepartment | null;
  category: RaCategory | null;
  level: RiskLevel | null;
  status: RiskStatus | null;
  from: string | null;
  to: string | null;
  text: string;
  cell: { likelihood: number; severity: number; mode: MatrixMode } | null;
}

export const EMPTY_FILTER: RiskFilter = {
  vesselId: null,
  department: null,
  category: null,
  level: null,
  status: null,
  from: null,
  to: null,
  text: '',
  cell: null,
};

export function filterRisks(items: readonly RiskSummary[], f: RiskFilter): RiskSummary[] {
  const text = f.text.trim().toLowerCase();
  return items.filter((i) => {
    if (f.vesselId && i.vesselId !== f.vesselId) return false;
    if (f.department && i.department !== f.department) return false;
    if (f.category && i.category !== f.category) return false;
    if (f.level && i.initial?.level !== f.level) return false;
    if (f.status && i.status !== f.status) return false;
    if (f.from && i.assessmentDay < f.from) return false;
    if (f.to && i.assessmentDay > f.to) return false;
    if (f.cell) {
      const r = f.cell.mode === 'initial' ? i.initial : i.residual;
      if (
        i.status === 'withdrawn' ||
        r?.likelihood !== f.cell.likelihood ||
        r.severity !== f.cell.severity
      ) {
        return false;
      }
    }
    if (text && !`${i.raNo} ${i.title} ${i.vesselName}`.toLowerCase().includes(text)) return false;
    return true;
  });
}

export type RiskSortKey = 'raNo' | 'title' | 'vessel' | 'department' | 'score' | 'status';

export function sortRisks(
  items: readonly RiskSummary[],
  sort: { key: RiskSortKey; dir: 'asc' | 'desc' },
): RiskSummary[] {
  const value = (i: RiskSummary): string | number => {
    switch (sort.key) {
      case 'raNo':
        return i.raNo;
      case 'title':
        return i.title.toLowerCase();
      case 'vessel':
        return i.vesselName.toLowerCase();
      case 'department':
        return i.department;
      case 'status':
        return i.status;
      default:
        return i.initial?.score ?? -1;
    }
  };
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    // Puansız değerlendirme her yönde sonda
    if (sort.key === 'score' && (x === -1) !== (y === -1)) return x === -1 ? 1 : -1;
    if (x < y) return -sign;
    if (x > y) return sign;
    return a.raNo.localeCompare(b.raNo);
  });
}

const csvCell = (value: string | number) => {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

/** Listenin CSV'si ("Export"); metinleri `label` çevirir (seviye, durum, departman). */
export function riskCsv(items: readonly RiskSummary[], label: (key: string) => string): string {
  const head = [
    'raNo',
    'title',
    'vessel',
    'department',
    'category',
    'level',
    'score',
    'residual',
    'status',
    'assessmentDay',
    'reviewDay',
  ];
  const rows = items.map((i) =>
    [
      i.raNo,
      i.title,
      i.vesselName,
      label(`risk.departments.${i.department}`),
      label(`risk.categories.${i.category}`),
      i.initial ? label(`risk.levels.${i.initial.level}`) : '',
      i.initial?.score ?? '',
      i.residual?.score ?? '',
      label(`risk.statuses.${i.status}`),
      i.assessmentDay,
      i.reviewDay,
    ]
      .map(csvCell)
      .join(','),
  );
  return [head.join(','), ...rows].join('\n');
}

export function newHazard(id: string): RiskHazard {
  return {
    id,
    hazard: '',
    consequence: '',
    likelihood: 3,
    severity: 3,
    residualLikelihood: 2,
    residualSeverity: 3,
  };
}

export function newControl(id: string, hazardId: string | null): RiskControl {
  return {
    id,
    hazardId,
    type: 'administrative',
    measure: '',
    responsible: '',
    dueDay: null,
    status: 'planned',
  };
}

/** Şablonun (ya da değerlendirmenin) ilk riski: en yüksek skorlu tehlike, verilen matrisle. */
export function templateRating(
  content: { hazards: readonly RiskHazard[] },
  matrix: RaMatrixDefinition = LEGACY_RISK_MATRIX,
): RiskRating | null {
  return riskSummary(content.hazards, matrix).initial;
}

/**
 * Değerlendirmenin seviyelendiği matris (ADR 0085 §4); bilgi yoksa ya da bantlar geçersizse
 * Legacy 5-band (eski sunucu ve gemi sürümü).
 */
export function matrixOfSummary(item: Pick<RiskSummary, 'matrix'>): RaMatrixDefinition {
  const bands = item.matrix?.bands;
  return bands && bands.length > 0 && raMatrixIssues({ bands }).length === 0
    ? { bands }
    : LEGACY_RISK_MATRIX;
}

/** Kısa yerel kimlik (tehlike ve önlem; sunucu şablondan kopyada UUIDv7 verir). */
export function localId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
